import type { AxiosInstance } from 'axios';
import type { paths } from '../schema';
import type { TimelineEntry } from '../types/timeline';
import type { RequestOptions, RequestOptionsWithParams } from './types';
import { curry1 } from './utils';

export type ServiceAccount = Mutable<
  paths['/api/service-accounts/{id}']['get']['responses']['200']['content']['application/json']
>;

export type GetServiceAccountsParams = Omit<
  NonNullable<paths['/api/service-accounts']['get']['parameters']['query']>,
  'ordering'
> & { search?: string; ordering?: string };

export type GetServiceAccountsResponse =
  paths['/api/service-accounts']['get']['responses']['200']['content']['application/json'];

export type CreateServiceAccountPayload = NonNullable<
  paths['/api/service-accounts']['post']['requestBody']
>['content']['application/json'];

export type CreateServiceAccountResponse =
  paths['/api/service-accounts']['post']['responses']['201']['content']['application/json'];

export type UpdateServiceAccountPayload = NonNullable<
  paths['/api/service-accounts/{id}']['patch']['requestBody']
>['content']['application/json'];

export type UpdateServiceAccountResponse =
  paths['/api/service-accounts/{id}']['patch']['responses']['200']['content']['application/json'];

export type RotateServiceAccountKeyResponse = Mutable<
  paths['/api/service-accounts/{id}/api-tokens/rotate']['post']['responses']['200']['content']['application/json']
>;

export type ServiceAccountTokenResponse = Mutable<
  paths['/api/service-accounts/{id}/api-tokens/primary']['get']['responses']['200']['content']['application/json']
>;

export type ServiceAccountHistoryParams = NonNullable<
  paths['/api/service-accounts/{employee_id}/history']['get']['parameters']['query']
>;

export type ServiceAccountTimelineEntry = TimelineEntry<Record<string, any>>;

export type ServiceAccountHistory = Omit<
  paths['/api/service-accounts/{employee_id}/history']['get']['responses']['200']['content']['application/json'],
  'results'
> & {
  results: ServiceAccountTimelineEntry[];
};

export const getServiceAccounts = async (
  instance: AxiosInstance,
  options: RequestOptionsWithParams<GetServiceAccountsParams> = {}
): Promise<GetServiceAccountsResponse> => {
  const res = await instance.get<GetServiceAccountsResponse>(
    '/service-accounts',
    options
  );
  return res.data;
};

export const getServiceAccount = async (
  instance: AxiosInstance,
  id: string,
  options?: RequestOptions
): Promise<ServiceAccount> => {
  const res = await instance.get<ServiceAccount>(
    `/service-accounts/${id}`,
    options
  );
  return res.data;
};

export const createServiceAccount = async (
  instance: AxiosInstance,
  payload: CreateServiceAccountPayload,
  options: RequestOptions = {}
): Promise<CreateServiceAccountResponse> => {
  const res = await instance.post<CreateServiceAccountResponse>(
    '/service-accounts',
    payload,
    options
  );
  return res.data;
};

export const updateServiceAccount = async (
  instance: AxiosInstance,
  id: string,
  payload: UpdateServiceAccountPayload,
  options: RequestOptions = {}
): Promise<UpdateServiceAccountResponse> => {
  const res = await instance.patch<UpdateServiceAccountResponse>(
    `/service-accounts/${id}`,
    payload,
    options
  );
  return res.data;
};

export const archiveServiceAccount = async (
  instance: AxiosInstance,
  id: string,
  options?: RequestOptions
): Promise<void> => {
  await instance.delete(`/service-accounts/${id}`, options);
};

export const getPrimaryToken = async (
  instance: AxiosInstance,
  id: string,
  options?: RequestOptions
): Promise<ServiceAccountTokenResponse> => {
  const res = await instance.get<ServiceAccountTokenResponse>(
    `/service-accounts/${id}/api-tokens/primary`,
    options
  );
  return res.data;
};

export const rotateServiceAccountToken = async (
  instance: AxiosInstance,
  id: string,
  options: RequestOptions = {}
): Promise<RotateServiceAccountKeyResponse> => {
  const res = await instance.post<RotateServiceAccountKeyResponse>(
    `/service-accounts/${id}/api-tokens/rotate`,
    {},
    options
  );
  return res.data;
};

export const getSecondaryToken = async (
  instance: AxiosInstance,
  id: string,
  options?: RequestOptions
): Promise<ServiceAccountTokenResponse> => {
  const res = await instance.get<ServiceAccountTokenResponse>(
    `/service-accounts/${id}/api-tokens/secondary`,
    options
  );
  return res.data;
};

export const deleteSecondaryToken = async (
  instance: AxiosInstance,
  id: string,
  options?: RequestOptions
): Promise<void> => {
  await instance.delete(
    `/service-accounts/${id}/api-tokens/secondary`,
    options
  );
};

export const getServiceAccountHistory = async (
  instance: AxiosInstance,
  id: string,
  options?: RequestOptionsWithParams<ServiceAccountHistoryParams>
): Promise<ServiceAccountHistory> => {
  const res = await instance.get<ServiceAccountHistory>(
    `/service-accounts/${id}/history`,
    options
  );
  return res.data;
};

export class ServiceAccountService {
  public getServiceAccounts;
  public getServiceAccount;
  public createServiceAccount;
  public updateServiceAccount;
  public archiveServiceAccount;
  public getPrimaryToken: (
    id: string,
    options?: RequestOptions
  ) => Promise<ServiceAccountTokenResponse>;
  public rotateServiceAccountToken;
  public getSecondaryToken: (
    id: string,
    options?: RequestOptions
  ) => Promise<ServiceAccountTokenResponse>;
  public deleteSecondaryToken;
  public getServiceAccountHistory;

  constructor(private instance: AxiosInstance) {
    this.getServiceAccounts = curry1(getServiceAccounts)(this.instance);
    this.getServiceAccount = curry1(getServiceAccount)(this.instance);
    this.createServiceAccount = curry1(createServiceAccount)(this.instance);
    this.updateServiceAccount = curry1(updateServiceAccount)(this.instance);
    this.archiveServiceAccount = curry1(archiveServiceAccount)(this.instance);
    this.getPrimaryToken = curry1(getPrimaryToken)(this.instance);
    this.rotateServiceAccountToken = curry1(rotateServiceAccountToken)(
      this.instance
    );
    this.getSecondaryToken = curry1(getSecondaryToken)(this.instance);
    this.deleteSecondaryToken = curry1(deleteSecondaryToken)(this.instance);
    this.getServiceAccountHistory = curry1(getServiceAccountHistory)(
      this.instance
    );
  }
}
